import {getIsBathroom} from "../../../../../../kvComponent/navigation/countertopSwitchers";

export default class Lighting {
  /**
   * @param {ThreeScene} sceneInstance – main ThreeScene instance (contains modelScale)
   * @param {THREE.WebGLRenderer} renderer
   * @param {THREE.Scene} scene
   */
  constructor(sceneInstance, renderer, scene) {
    this.sceneInstance = sceneInstance;
    this.renderer      = renderer;
    this.scene         = scene;
    this.useEnhanced3DQuality = sceneInstance.enhanced3DQuality === true;

    // Ceiling spot parameters (in inches)
    this.CEILING_HEIGHT_INCHES      = 150; // height of ceiling above floor
    this.CEILING_SPOT_OFFSET_INCHES = this.useEnhanced3DQuality ? -90 : -100;  // how far back from cabinet front
    this.CEILING_SPOT_INTENCITY     = this.useEnhanced3DQuality ? 4.2 : 5.5;
    this.CEILING_SPOT_COLOR         = this.useEnhanced3DQuality ? 0xfff7ee : 0xffffff;
    this.CEILING_SPOT_ANGLE         = this.useEnhanced3DQuality ? 0.72 : 0.6;
    this.CEILING_SPOT_PENUMBRA      = this.useEnhanced3DQuality ? 0.62 : 0.3;
    this.CEILING_SPOT_DECAY         = this.useEnhanced3DQuality ? 1 : 0.5;
    this.CEILING_SPOT_DISTANCE_INCHES = this.useEnhanced3DQuality ? 220 : 200;

    // Front fill light parameters (in inches)
    this.FILL_HEIGHT_INCHES = 20; // mid-height of upper cabinets
    this.FILL_OFFSET_INCHES = 75; // distance in front of cabinets
    this.KITCHEN_FILL_INTENSITY = 0.85;
    this.BATHROOM_FILL_INTENSITY = this.useEnhanced3DQuality ? 2.1 : 3.5;
    this.FILL_DISTANCE_INCHES = this.useEnhanced3DQuality ? 130 : 100;
    this.FILL_ANGLE = this.useEnhanced3DQuality ? 0.95 : 0.8; // spotlight cone angle
    this.FILL_PENUMBRA = this.useEnhanced3DQuality ? 0.75 : 0.4; // soft edge
    this.FILL_COLOR = this.useEnhanced3DQuality ? 0xf4f7ff : 0xffffff;
    this.HEMI_SKY_COLOR = this.useEnhanced3DQuality ? 0xf7f9ff : 0xffffff;
    this.HEMI_GROUND_COLOR = this.useEnhanced3DQuality ? 0x8a8277 : 0x444444;
    this.HEMI_INTENSITY = this.useEnhanced3DQuality ? 0.48 : 1.2;

    // Minimum distances between lights (in inches) to avoid overlap
    this.SPOT_MIN_DISTANCE_INCHES = 20;
    this.FILL_MIN_DISTANCE_INCHES = 25;

    this.cabinetLights = [];

    // Note: lower cabinet lights omitted for brevity
  }

  _addCabinetLight(light) {
    light.userData = {
      ...light.userData,
      isCabinetLighting: true,
    };
    this.cabinetLights.push(light);
    this.scene.add(light);
  }

  clearCabinetLights() {
    this.cabinetLights.forEach((light) => {
      if (light.parent) {
        light.parent.remove(light);
      }

      if (light.target && light.target.parent) {
        light.target.parent.remove(light.target);
      }

      if (light.dispose) {
        light.dispose();
      }
    });
    this.cabinetLights = [];
  }

  updateCabinetLights(frontLinesUpper) {
    this.clearCabinetLights();
    this.addCabinetLights(frontLinesUpper);
  }

  /**
   * Compute all light positions from front-line segments.
   * Returns ceiling spot positions, target positions, and front fill positions.
   *
   * @param {Array<{from:{x,y}, to:{x,y}, shapeCenter?:{x,y}}>} lines
   * @returns {Array<{lightPos:{x,y,z}, targetPos:{x,y,z}, frontFillPos:{x,y,z}}>}
   */
  getSpotPositionsFromFrontLines(lines) {
    return lines.map(line => {
      // 1. Edge vector and length
      const dx = line.to.x - line.from.x;
      const dy = line.to.y - line.from.y;
      const len = Math.hypot(dx, dy) || 1;

      // 2. Calculate normals
      const midX = (line.from.x + line.to.x) / 2;
      const midY = (line.from.y + line.to.y) / 2;
      const center = line.shapeCenter || { x: midX, y: midY };

      // Inward normal (assumes clockwise winding): (-dy, dx) normalized
      let inwardNX = -dy / len;
      let inwardNZ =  dx / len;

      // Determine correct inward normal direction using shapeCenter
      const dotInward = (center.x - midX) * inwardNX + (center.y - midY) * inwardNZ;
      if (dotInward < 0) {
        inwardNX = -inwardNX;
        inwardNZ = -inwardNZ;
      }

      // Outward normal is opposite of inward
      const outwardNX = -inwardNX;
      const outwardNZ = -inwardNZ;

      // 3. Generate all three positions
      return {
        // Ceiling spotlight position (with inward offset)
        lightPos: {
          x: midX + inwardNX * this.CEILING_SPOT_OFFSET_INCHES,
          y: this.CEILING_HEIGHT_INCHES,
          z: midY + inwardNZ * this.CEILING_SPOT_OFFSET_INCHES
        },
        // Target position on cabinet front
        targetPos: {
          x: midX,
          y: 0,
          z: midY
        },
        // Front fill light position (with outward offset)
        frontFillPos: {
          x: midX + outwardNX * this.FILL_OFFSET_INCHES,
          y: this.FILL_HEIGHT_INCHES,
          z: midY + outwardNZ * this.FILL_OFFSET_INCHES
        }
      };
    });
  }

  /**
   * Universal filter for positions - removes points that are too close to each other.
   *
   * @param {Array} items - array of items to filter
   * @param {string} posKey - key name to access position object (e.g., 'lightPos', 'frontFillPos')
   * @param {number} minDist - minimum allowed distance between positions (in inches)
   * @returns {Array} filtered array
   */
  filterUniquePositions(items, posKey, minDist) {
    const unique = [];
    items.forEach(item => {
      const pos = item[posKey];
      const isFar = unique.every(u => {
        const uPos = u[posKey];
        const dx = pos.x - uPos.x;
        const dz = pos.z - uPos.z;
        return Math.hypot(dx, dz) > minDist;
      });
      if (isFar) {
        unique.push(item);
      }
    });
    return unique;
  }

  /**
   * Given an array of items with position key, returns a Set of indices corresponding to:
   *  - the four corners (upper-left, upper-right, lower-left, lower-right)
   *  - one center point
   *
   * @param {Array} items - array of items
   * @param {string} posKey - key name to access position object
   * @returns {Set<number>}
   */
  computeShadowIndices(items, posKey) {
    // 1. Compute bounding‐box extents in XZ
    const xs = items.map(item => item[posKey].x);
    const zs = items.map(item => item[posKey].z);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minZ = Math.min(...zs), maxZ = Math.max(...zs);

    // 2. Find the index closest to each corner
    const corners = [
      { x: minX, z: maxZ },  // upper-left
      { x: maxX, z: maxZ },  // upper-right
      { x: minX, z: minZ },  // lower-left
      { x: maxX, z: minZ }   // lower-right
    ];
    const cornerIndices = corners.map(c => {
      let bestIdx = 0, bestDist = Infinity;
      items.forEach((item, i) => {
        const pos = item[posKey];
        const dx = pos.x - c.x;
        const dz = pos.z - c.z;
        const d2 = dx*dx + dz*dz;
        if (d2 < bestDist) {
          bestDist = d2;
          bestIdx  = i;
        }
      });
      return bestIdx;
    });

    // 3. Compute the average (center) point and find nearest index
    const avg = items.reduce((acc, item) => {
      acc.x += item[posKey].x;
      acc.z += item[posKey].z;
      return acc;
    }, { x: 0, z: 0 });
    avg.x /= items.length;
    avg.z /= items.length;

    let centerIdx = 0, minCenterDist = Infinity;
    items.forEach((item, i) => {
      const pos = item[posKey];
      const dx = pos.x - avg.x;
      const dz = pos.z - avg.z;
      const d2 = dx*dx + dz*dz;
      if (d2 < minCenterDist) {
        minCenterDist  = d2;
        centerIdx      = i;
      }
    });

    // 4. Merge corner + center indices into a Set
    return new Set([...cornerIndices, centerIdx]);
  }

  /**
   * Add ceiling spotlights above upper cabinets.
   *
   * @param {Array<{lightPos:{x,y,z}, targetPos:{x,y,z}}>} spotData - filtered spotlight data
   */
  addUpperLights(spotData) {
    const { THREE } = window;
    const scale = this.sceneInstance.modelScale;

    // Compute which indices should cast shadows
    const shadowIndices = this.computeShadowIndices(spotData, 'lightPos');

    // Create SpotLights
    spotData.forEach((data, idx) => {
      const lightX = data.lightPos.x * scale;
      const lightY = data.lightPos.y * scale;
      const lightZ = data.lightPos.z * scale;

      const targetX = data.targetPos.x * scale;
      const targetY = data.targetPos.y * scale;
      const targetZ = data.targetPos.z * scale;

      const spot = new THREE.SpotLight(this.CEILING_SPOT_COLOR, this.CEILING_SPOT_INTENCITY);
      spot.position.set(lightX, lightY, lightZ);

      // Aim at cabinet front position
      spot.target.position.set(targetX, targetY, targetZ);
      spot.lookAt(new THREE.Vector3(targetX, targetY, targetZ));

      spot.angle    = this.CEILING_SPOT_ANGLE;
      spot.penumbra = this.CEILING_SPOT_PENUMBRA;
      spot.decay    = this.CEILING_SPOT_DECAY;
      spot.distance = this.CEILING_SPOT_DISTANCE_INCHES * scale;

      // only corner+center spots cast shadows
      spot.castShadow = shadowIndices.has(idx);

      spot.shadow.mapSize.set(1024, 1024);
      spot.shadow.bias       = this.useEnhanced3DQuality ? -0.00035 : -0.0005;
      spot.shadow.normalBias = this.useEnhanced3DQuality ? 0.04 : 0.05;
      spot.shadow.radius     = this.useEnhanced3DQuality ? 6 : 4;
      if (this.useEnhanced3DQuality) {
        spot.shadow.camera.near = 10 * scale;
        spot.shadow.camera.far  = this.CEILING_SPOT_DISTANCE_INCHES * scale;
      }

      this._addCabinetLight(spot);
      this.scene.add(spot.target);
    });
  }

  /**
   * Add frontal spotlights to illuminate cabinet fronts.
   *
   * @param {Array<{frontFillPos:{x,y,z}, targetPos:{x,y,z}}>} fillData - filtered front fill light data
   */
  addFrontFillLights(fillData, intensity) {
    const { THREE } = window;
    const scale = this.sceneInstance.modelScale;

    // Create SpotLights
    fillData.forEach(data => {
      const lightX = data.frontFillPos.x * scale;
      const lightY = data.frontFillPos.y * scale;
      const lightZ = data.frontFillPos.z * scale;

      const targetX = data.targetPos.x * scale;
      //const targetY = data.targetPos.y * scale;
      const targetZ = data.targetPos.z * scale;

      const fillSpot = new THREE.SpotLight(this.FILL_COLOR, intensity, this.FILL_DISTANCE_INCHES * scale);
      fillSpot.position.set(lightX, lightY, lightZ);

      // Aim at cabinet front - this makes the spotlight point toward the cabinet
      fillSpot.target.position.set(targetX, lightY, targetZ);
      fillSpot.lookAt(new THREE.Vector3(targetX, lightY, targetZ));

      fillSpot.angle = this.FILL_ANGLE;
      fillSpot.penumbra = this.FILL_PENUMBRA;
      fillSpot.decay = 0.5;
      fillSpot.castShadow = false; // fill lights don't cast shadows

      this._addCabinetLight(fillSpot);
      this.scene.add(fillSpot.target);
    });
  }

  /**
   * Main method to add all cabinet lighting (ceiling spots + front fill lights).
   *
   * @param {Array<{from:{x,y}, to:{x,y}, shapeCenter?:{x,y}}>} frontLinesUpper
   */
  addCabinetLights(frontLinesUpper) {
    if (!frontLinesUpper || !frontLinesUpper.length) {
      return;
    }

    // 1. Generate all light positions from front lines
    const allPositions = this.getSpotPositionsFromFrontLines(frontLinesUpper);

    // 2. Filter spotlight positions (remove spots that are too close)
    const filteredSpots = this.filterUniquePositions(
      allPositions,
      'lightPos',
      this.SPOT_MIN_DISTANCE_INCHES
    );

    // 3. Filter front fill positions (remove fills that are too close)
    const filteredFills = this.filterUniquePositions(
      allPositions,
      'frontFillPos',
      this.FILL_MIN_DISTANCE_INCHES
    );

    // 4. Add lights to scene
    const isBathroom = getIsBathroom();
    this.addUpperLights(filteredSpots);
    if (isBathroom) {
      this.addFrontFillLights(filteredFills, this.BATHROOM_FILL_INTENSITY);
    } else if (this.useEnhanced3DQuality && !this.sceneInstance.isMobile) {
      this.addFrontFillLights(filteredFills, this.KITCHEN_FILL_INTENSITY);
    }

    const hemi = new window.THREE.HemisphereLight(
      this.HEMI_SKY_COLOR,
      this.HEMI_GROUND_COLOR,
      this.HEMI_INTENSITY
    );
    this._addCabinetLight(hemi);
  }
}
